import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps } from 'react';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { isTypeFn } from '@sorare/core/src/gql';
import {
  so5RewardConfigsToSimpleRewards,
  so5RewardsToSimpleRewards,
} from '@sorare/core/src/lib/rewards';

import { ClickableRewardOverview } from 'components/rewards/ClickableRewardOverview';

import {
  useSo5RewardOrConfigSimpleRewards_so5League,
  useSo5RewardOrConfigSimpleRewards_so5RewardOrConfig,
} from './__generated__/useSo5RewardOrConfigSimpleRewards.graphql';

type Props = {
  so5RewardOrConfigs: useSo5RewardOrConfigSimpleRewards_so5RewardOrConfig[];
  so5League?: useSo5RewardOrConfigSimpleRewards_so5League;
  rarity: Rarity | null;
} & Pick<ComponentProps<typeof ClickableRewardOverview>, 'variant'>;

export const useSo5RewardOrConfigSimpleRewards = ({
  so5RewardOrConfigs,
  rarity,
  so5League,
  ...rewardOverviewProps
}: Props) => {
  const { exchangeRate } = useConfigContext();

  const so5RewardsWithSimpleRewards = so5RewardOrConfigs
    .filter(isTypeFn('So5Reward'))
    .flatMap(so5Reward => {
      const simpleRewards = so5RewardsToSimpleRewards({
        so5Rewards: [so5Reward],
        rarity,
        rates: exchangeRate.ethRates,
      });

      return simpleRewards.map(simpleReward => ({
        simpleReward,
        probabilisticBundleConfigs: so5Reward.probabilisticBundle
          ? [so5Reward.probabilisticBundle.config]
          : [],
        deliverableItems:
          so5Reward.deliverableItems
            ?.map(item => item.deliverableItem)
            .filter(Boolean) || [],
        experiences: [],
      }));
    });

  const so5RewardConfigsWithSimpleRewards = so5RewardOrConfigs
    .filter(isTypeFn('So5RewardConfig'))
    .flatMap(so5RewardConfig => {
      const simpleRewards = so5RewardConfigsToSimpleRewards({
        so5RewardConfigs: [so5RewardConfig],
        rarity,
        rates: exchangeRate.ethRates,
      });

      return simpleRewards.map(simpleReward => ({
        simpleReward: {
          ...simpleReward,
          count: simpleReward.sharedPool
            ? simpleReward.count / (simpleReward.poolSize || 1)
            : simpleReward.count,
        },
        probabilisticBundleConfigs:
          so5RewardConfig.probabilisticBundleConfigs || [],
        deliverableItems:
          so5RewardConfig.deliverableItems
            ?.map(item => item.deliverableItem)
            .filter(Boolean) || [],
        experiences: so5RewardConfig.experiences || [],
      }));
    });

  const simpleRewards = [
    ...so5RewardsWithSimpleRewards,
    ...so5RewardConfigsWithSimpleRewards,
  ];

  return {
    simpleRewards,
    clickableRewardsOverview: simpleRewards.map(
      (
        {
          simpleReward,
          deliverableItems,
          probabilisticBundleConfigs,
          experiences,
        },
        i
      ) => (
        <ClickableRewardOverview
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          {...rewardOverviewProps}
          simpleReward={simpleReward}
          deliverableItems={deliverableItems}
          probabilisticBundleConfigs={probabilisticBundleConfigs}
          experiences={experiences}
          so5League={so5League}
        />
      )
    ),
  };
};

useSo5RewardOrConfigSimpleRewards.fragments = {
  so5RewardOrConfig: gql`
    fragment useSo5RewardOrConfigSimpleRewards_so5RewardOrConfig on So5RewardOrConfig {
      ... on So5Reward {
        slug
        deliverableItems {
          deliverableItem {
            slug
            ...ClickableRewardOverview_deliverableItem
          }
        }
        probabilisticBundle {
          id
          config {
            id
            ...ClickableRewardOverview_probabilisticBundleConfig
          }
        }
        ...so5RewardsToSimpleRewards_so5Reward
      }
      ... on So5RewardConfig {
        deliverableItems {
          deliverableItem {
            slug
            ...ClickableRewardOverview_deliverableItem
          }
        }
        probabilisticBundleConfigs {
          id
          ...ClickableRewardOverview_probabilisticBundleConfig
        }
        experiences {
          ...ClickableRewardOverview_so5RewardExperience
        }
        ...so5RewardConfigsToSimpleRewards_so5RewardConfig
      }
    }
    ${so5RewardConfigsToSimpleRewards.fragments.so5RewardConfig}
    ${so5RewardsToSimpleRewards.fragments.so5Reward}
    ${ClickableRewardOverview.fragments.deliverableItem}
    ${ClickableRewardOverview.fragments.probabilisticBundleConfig}
    ${ClickableRewardOverview.fragments.so5RewardExperience}
  ` as TypedDocumentNode<useSo5RewardOrConfigSimpleRewards_so5RewardOrConfig>,
  so5League: gql`
    fragment useSo5RewardOrConfigSimpleRewards_so5League on So5League {
      slug
      ...ClickableRewardOverview_so5League
    }
    ${ClickableRewardOverview.fragments.so5League}
  ` as TypedDocumentNode<useSo5RewardOrConfigSimpleRewards_so5League>,
};
